import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Skeleton } from "@/components/ui/skeleton";
import type { CartFreightState } from "@/hooks/checkout/use-cart-freight-quote";
import { formatCents } from "@/lib/formatting/currency";
import { checkoutDeliveryCents } from "@shared/orders/checkout-quote-summary";

interface CheckoutSummaryRowsProps {
  itemCount: number;
  goodsCents: number;
  totalCents: number;
  freight: CartFreightState;
  complete: boolean;
  loadCount: number;
  goodsLabel?: string;
  compact?: boolean;
  onAddDelivery?: () => void;
}

export function CheckoutSummaryRows({
  itemCount,
  goodsCents,
  totalCents,
  freight,
  complete,
  loadCount,
  goodsLabel,
  compact = false,
  onAddDelivery,
}: CheckoutSummaryRowsProps) {
  const quoted = complete && freight.quote !== null && !freight.loading;
  return (
    <>
      <dl className={compact ? "space-y-2 text-sm" : "mt-4 space-y-3 text-sm"}>
        <Row label={goodsLabel ?? productCountLabel(itemCount)}>
          {formatCents(goodsCents)}
        </Row>
        <Row label={loadCount > 1 ? `Delivery (${loadCount} trucks)` : "Delivery"}>
          <FreightAmount freight={freight} complete={complete} onAddDelivery={onAddDelivery} />
        </Row>
        {freight.quote?.liftGateCents ? (
          <Row label="Lift gate">{formatCents(freight.quote.liftGateCents)}</Row>
        ) : null}
        <Row label="Tax">
          <span className="text-muted-foreground">Shown at payment</span>
        </Row>
      </dl>
      <Separator className={compact ? "my-3" : "my-4"} />
      <TotalBeforeTax
        cents={quoted ? totalCents : goodsCents}
        plusDelivery={!quoted}
      />
    </>
  );
}

function productCountLabel(itemCount: number) {
  return `${itemCount} ${itemCount === 1 ? "product" : "products"}`;
}

function TotalBeforeTax({
  cents,
  plusDelivery,
}: {
  cents: number;
  plusDelivery: boolean;
}) {
  return (
    <div className="flex justify-between gap-3 font-semibold">
      <span className="whitespace-nowrap">
        {plusDelivery ? "Subtotal" : "Total before tax"}
      </span>
      <span className="text-right tabular-nums">
        {formatCents(cents)}
        {plusDelivery ? (
          <span className="block text-xs font-normal text-muted-foreground">
            + delivery
          </span>
        ) : null}
      </span>
    </div>
  );
}

function Row({
  label,
  children,
}: {
  label: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <div className="flex justify-between gap-3">
      <dt>{label}</dt>
      <dd className="text-right tabular-nums">{children}</dd>
    </div>
  );
}

function FreightAmount({
  freight,
  complete,
  onAddDelivery,
}: {
  freight: CartFreightState;
  complete: boolean;
  onAddDelivery?: () => void;
}) {
  if (freight.loading) return <Skeleton className="ml-auto h-4 w-16" />;
  if (freight.error)
    return <span className="text-destructive-strong">Couldn&apos;t quote</span>;
  if (!freight.quote || !complete) {
    if (!onAddDelivery) {
      return <span className="text-muted-foreground">Add delivery details</span>;
    }
    return (
      <Button
        type="button"
        variant="link"
        size="bare"
        className="min-h-11 min-w-11"
        onClick={onAddDelivery}
      >
        Add delivery details
      </Button>
    );
  }
  return <>{formatCents(checkoutDeliveryCents(freight.quote))}</>;
}
